import {
  AccordionDetails,
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  Accordion as MuiAccordion,
  AccordionSummary as MuiAccordionSummary,
  Checkbox as MuiCheckbox,
  FormControlLabel as MuiFormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

import { StandardRead } from "@/services/generatedApi";
import { StandardSelect } from "@/ui-components/layouts/components/AssessmentsMenu/components/StandardSelect/StandardSelect";
import { Filters } from "@/ui-components/layouts/components/AssessmentsMenu/types";
import styled from "@emotion/styled";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

const Accordion = styled(MuiAccordion)({
  ".MuiAccordionSummary-root.Mui-expanded": {
    background: "var(--Soft-Black-Gray-Scale-200, #F2F2F2)",
  },
  ".MuiAccordionSummary-root:not(.Mui-expanded)": {
    borderRadius: "8px",
  },
});

const AccordionSummary = styled(MuiAccordionSummary)({
  overflow: "hidden",
  color: "var(--Soft-Black-Gray-Scale-800, #4A4A4A)",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  fontSize: "14px",
  fontWeight: "700",
  lineHeight: "20px",
  textTransform: "uppercase",
});

const Checkbox = styled(MuiCheckbox)({
  ".MuiFormControlLabel-label": {
    fontSize: "14px",
    fontWeight: "600",
  },
});

const FormControlLabel = styled(MuiFormControlLabel)({
  ".MuiFormControlLabel-label": {
    fontSize: "14px",
    fontWeight: "600",
  },
});

type FilterDrawerProps = {
  open: boolean;
  onClose: () => void;
  filters: Filters;
  onChange: (filters: Filters, standard: string) => void;
  standards: StandardRead[];
  selectedStandard: string;
};

type StyledCheckboxProps = {
  checked: boolean;
  indeterminate?: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  padding?: number;
};

const StyledCheckbox: React.FC<StyledCheckboxProps> = ({ checked, indeterminate, onChange, label, padding = 6 }) => (
  <FormControlLabel
    control={
      <Checkbox
        checked={checked}
        indeterminate={indeterminate}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => onChange(event.target.checked)}
      />
    }
    label={label}
    sx={{ mx: 0, pl: padding, background: checked || indeterminate ? "#EDF2F0" : "unset" }}
  />
);

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filters,
  standards,
  selectedStandard,
  onChange,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = React.useState(() => {
    const { isAnswered, isNotAnswered, unassigned, inReview, declined, reviewed } = filters;
    return isAnswered || isNotAnswered || unassigned || inReview || declined || reviewed;
  });
  const [assigned, setAssigned] = React.useState(filters.assigned ? "true" : "false");
  const [isAnswered, setIsAnswered] = React.useState(!!filters.isAnswered);
  const [isNotAnswered, setIsNotAnswered] = React.useState(!!filters.isNotAnswered);
  const [unassigned, setUnassigned] = React.useState(!!filters.unassigned);
  const [inReview, setInReview] = React.useState(!!filters.inReview);
  const [declined, setDeclined] = React.useState(!!filters.declined);
  const [reviewed, setReviewed] = React.useState(!!filters.reviewed);

  const [standard, setStandard] = React.useState(selectedStandard);

  const allSelected = isAnswered && isNotAnswered && inReview && unassigned && declined && reviewed;
  const noneSelected = !isAnswered && !isNotAnswered && !inReview && !unassigned && !declined && !reviewed;

  useEffect(() => {
    setAssigned(filters.assigned ? "true" : "false");
    setIsAnswered(!!filters.isAnswered);
    setIsNotAnswered(!!filters.isNotAnswered);
    setUnassigned(!!filters.unassigned);
    setInReview(!!filters.inReview);
    setDeclined(!!filters.declined);
    setReviewed(!!filters.reviewed);
    setStandard(selectedStandard);
  }, [filters, selectedStandard]);

  const onSave = () => {
    onChange(
      {
        assigned: assigned === "true",
        isAnswered,
        isNotAnswered,
        unassigned,
        inReview,
        declined,
        reviewed,
      },
      standard,
    );
    onClose();
  };

  return (
    <Drawer
      open={open}
      sx={{
        "& .MuiDrawer-paper": {
          width: "320px",
          paddingTop: "72px",
        },
      }}
      onClose={onClose}
    >
      <Box sx={{ padding: "0 var(--gap-lg, 16px);", paddingTop: "var(--gap-lg, 16px);", height: "100%" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontSize: "18px", fontWeight: "600", lineHeight: "24px" }}>
            {t("assessments.Filters")}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
        <Box sx={{ height: "calc(100% - 120px)" }}>
          <Box>
            <FormControl>
              <RadioGroup
                row
                aria-labelledby="demo-row-radio-buttons-group-label"
                name="row-radio-buttons-group"
                value={assigned}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setAssigned((event.target as HTMLInputElement).value)
                }
                sx={{
                  ".MuiFormControlLabel-label": {
                    overflow: "hidden",
                    color: "var(--Soft-Black-Gray-Scale-800, #4A4A4A)",
                    textOverflow: "ellipsis",
                    fontSize: "14px",
                    fontWeight: "600",
                    lineHeight: "20px",
                  },
                }}
              >
                <FormControlLabel value="true" control={<Radio />} label={t("assessments.status.assigned_to_me")} />
                <FormControlLabel value="false" control={<Radio />} label={t("assessments.status.all_questions")} />
              </RadioGroup>
            </FormControl>
          </Box>
          <Box sx={{ my: "16px" }}>
            <StandardSelect
              standards={standards}
              selectedStandard={standard}
              onStandardChange={value => setStandard(String(value))}
            />
          </Box>
          <Box>
            <Accordion expanded={expanded ?? false} onChange={() => setExpanded(state => !state)}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel2-content" id="panel2-header">
                {t("assessments.Question_status")}
              </AccordionSummary>
              <AccordionDetails sx={{ padding: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <StyledCheckbox
                    label={t("general.All")}
                    checked={allSelected}
                    indeterminate={!allSelected && !noneSelected}
                    padding={3}
                    onChange={checked => {
                      setIsAnswered(checked);
                      setIsNotAnswered(checked);
                      setInReview(checked);
                      setUnassigned(checked);
                      setDeclined(checked);
                      setReviewed(checked);
                    }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <StyledCheckbox
                      label={t("assessments.status.unassigned")}
                      checked={unassigned}
                      onChange={setUnassigned}
                    />
                    <StyledCheckbox
                      label={t("assessments.status.in_draft")}
                      checked={isAnswered && isNotAnswered}
                      indeterminate={isAnswered !== isNotAnswered}
                      onChange={checked => {
                        setIsAnswered(checked);
                        setIsNotAnswered(checked);
                      }}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <StyledCheckbox
                        label={t("assessments.status.answered")}
                        checked={isAnswered}
                        onChange={setIsAnswered}
                        padding={9}
                      />
                      <StyledCheckbox
                        label={t("assessments.status.not_answered")}
                        checked={isNotAnswered}
                        onChange={setIsNotAnswered}
                        padding={9}
                      />
                    </Box>
                    <StyledCheckbox
                      label={t("assessments.status.in_review")}
                      checked={inReview}
                      onChange={setInReview}
                    />
                    <StyledCheckbox
                      label={t("assessments.status.declined")}
                      checked={declined}
                      onChange={setDeclined}
                    />
                    <StyledCheckbox
                      label={t("assessments.status.reviewed")}
                      checked={reviewed}
                      onChange={setReviewed}
                    />
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <Button variant="outlined" onClick={onClose} sx={{ mr: "16px" }}>
            {t("general.Cancel")}
          </Button>
          <Button variant="contained" onClick={onSave}>
            {t("general.Apply")}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};
